import React, { useState } from "react";
import { Btn } from "@/lib/ui";
import { cn } from "@/lib/utils";
import { Plus, Search, Pencil, Archive, Trash2, MessageSquare } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

export function HistorySidebar({
  items = [], activeId, onSelect, onNew, onRename, onArchive, onDelete,
  newLabel = "New", heading = "History", loading = false, testidPrefix = "history",
  showArchived = false, onToggleArchived,
}) {
  const [q, setQ] = useState("");
  const filtered = items.filter((it) => (it.title || "").toLowerCase().includes(q.toLowerCase()));

  const rename = (it) => {
    const next = window.prompt("Rename", it.title || "");
    if (next && next.trim() && next.trim() !== it.title) onRename(it.id, next.trim());
  };
  const del = (it) => {
    if (window.confirm(`Delete "${it.title || "this item"}"? This cannot be undone.`)) onDelete(it.id);
  };

  return (
    <div className="flex flex-col h-full w-full" data-testid={`${testidPrefix}-sidebar`}>
      <Btn onClick={onNew} className="w-full mb-3" data-testid={`${testidPrefix}-new`}>
        <Plus className="h-4 w-4" /> {newLabel}
      </Btn>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          data-testid={`${testidPrefix}-search`}
          className="w-full h-8 rounded-md border border-border bg-surface pl-8 pr-3 text-xs focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
        />
      </div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 px-1">{heading}</div>
      <div className="flex-1 overflow-y-auto space-y-1 pr-0.5" data-testid={`${testidPrefix}-list`}>
        {loading && <div className="text-xs text-muted-foreground px-1 py-2">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-xs text-muted-foreground px-1 py-2">No items yet.</div>
        )}
        {filtered.map((it) => (
          <div
            key={it.id}
            onClick={() => onSelect(it.id)}
            data-testid={`${testidPrefix}-item`}
            className={cn(
              "group cursor-pointer rounded-md border px-2.5 py-2 transition-colors",
              activeId === it.id
                ? "border-primary/50 bg-primary/10"
                : "border-border/60 bg-surface/40 hover:border-border hover:bg-surface",
            )}
          >
            <div className="flex items-start gap-2">
              <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <div className="text-xs font-medium text-foreground/90 truncate">{it.title || "Untitled"}</div>
                  {it.archived && <span className="text-[9px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1 shrink-0">Archived</span>}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono">{relTime(it.updated_at || it.created_at)}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button title="Rename" data-testid={`${testidPrefix}-rename`}
                onClick={(e) => { e.stopPropagation(); rename(it); }}
                className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground">
                <Pencil className="h-3 w-3" />
              </button>
              <button title={it.archived ? "Unarchive" : "Archive"} data-testid={`${testidPrefix}-archive`}
                onClick={(e) => { e.stopPropagation(); onArchive(it.id, !it.archived); }}
                className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground">
                <Archive className="h-3 w-3" />
              </button>
              <button title="Delete" data-testid={`${testidPrefix}-delete`}
                onClick={(e) => { e.stopPropagation(); del(it); }}
                className="p-1 rounded hover:bg-err/10 text-muted-foreground hover:text-err">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {onToggleArchived && (
        <button data-testid={`${testidPrefix}-toggle-archived`}
          onClick={() => onToggleArchived(!showArchived)}
          className="mt-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors text-left px-1">
          {showArchived ? "← Hide archived" : "Show archived"}
        </button>
      )}
    </div>
  );
}
